import * as React from "react"
import { useParams, Link } from "react-router"
import { useQuery } from "@tanstack/react-query"

import { api } from "@/lib/api"
import { audienceLabel } from "@/lib/audienceLabels"
import { SectionCard } from "@/components/console/SectionCard"
import { PageSkeleton, FetchError } from "@/components/console/Fallbacks"
import { Switch } from "@/components/ui/switch"

export function Audience({ token }: { token: string }) {
  const { audience = "" } = useParams()
  const [showRetired, setShowRetired] = React.useState(false)
  const records = useQuery({
    queryKey: ["records", audience, showRetired, token],
    queryFn: () => api.records(token, audience, showRetired ? "all" : "active"),
  })

  if (records.isPending) return <PageSkeleton />
  if (records.isError) return <FetchError error={records.error} retry={() => records.refetch()} />

  return (
    <SectionCard
      title={audienceLabel(audience)}
      right={
        <div className="flex items-center gap-4">
          <Link
            to={`/audiences/${encodeURIComponent(audience)}/maintenance`}
            className="font-mono text-xs text-primary hover:underline"
          >
            maintenance
          </Link>
          <label className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
            <Switch checked={showRetired} onCheckedChange={setShowRetired} />
            show retired
          </label>
        </div>
      }
    >
      {records.data.length === 0 ? (
        <p className="font-mono text-xs text-muted-foreground">Nothing written down here yet.</p>
      ) : (
        <ul className="space-y-1">
          {records.data.map((r) => (
            <li key={r.id} className="flex items-center justify-between">
              <Link
                to={`/records/${encodeURIComponent(audience)}/${r.id}`}
                className="font-mono text-xs text-foreground hover:underline"
              >
                {r.title}
              </Link>
              {r.status === "retired" ? (
                <span className="text-[10px] text-muted-foreground">retired</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <Link
        to={`/records/new?audience=${encodeURIComponent(audience)}`}
        className="mt-3 inline-block font-mono text-xs text-primary hover:underline"
      >
        + new record
      </Link>
    </SectionCard>
  )
}
